import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { TextField, TableFooter, Button, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { ResourceLoading } from "../../utils/ResourceLoading";
import ItemSelector from "../../utils/Selector/ItemSelector";
import {
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import ProConfirmation from "../../utils/ProConfirmation";
import { v4 as uuidv4 } from "uuid";
import { TWclassNames } from "../../utils/Div";
import { PMField } from "../../utils/PMField";
import ItemSelectorV2 from "../../utils/Selector/ItemSelectorV2";
import { useDispatch, useSelector } from "react-redux";
import { getStorages } from "../../../slices/inventory/storage";
import { fetchProduct } from "../../../api/product";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  // Update row_order to maintain consistency
  return result.map((item, index) => ({
    ...item,
    row_order: index,
  }));
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,
  background: isDragging ? "lightgrey" : null,
  ...draggableStyle,
});

const MemoizedGroupRow = React.memo(
  ({
    item,
    provided,
    snapshot,
    deleteRow,
    onItemRowChange,
    groupFieldDisabled,
  }) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted

    return (
      <TableRow
        className="bg-gray-100"
        // sx={{
        //   backgroundColor: "#bec0c2",
        // }}
        hover
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <TableCell>
          <DragHandleIcon />
        </TableCell>
        <TableCell align="left" colSpan={3}>
          <TextField
            label="Title"
            variant="standard"
            name="title"
            sx={{ width: "50%" }}
            value={item.title}
            onChange={(e) => onItemRowChange(e, item)}
            disabled={groupFieldDisabled}
          ></TextField>
        </TableCell>
        <TableCell align="left" colSpan={6}>
          <TextField
            label="Kommentar"
            variant="standard"
            name="comment"
            fullWidth
            multiline
            rows={2}
            value={item.comment}
            onChange={(e) => onItemRowChange(e, item)}
            disabled={groupFieldDisabled}
          ></TextField>
        </TableCell>

        <TableCell align="right">
          <TrashIcon
            className="cursor-pointer h-5 h-5 text-red-400"
            onClick={() => deleteRow(item)}
          />
        </TableCell>
      </TableRow>
    );
  }
);
MemoizedGroupRow.displayName = "MemoizedGroupRow"; // Add display name

const MemoizedItemRow = React.memo(
  ({
    item,
    provided,
    snapshot,
    props,
    deleteRow,
    onItemRowChange,
    handleRowProductSelect,
    calculateRowTotalPrice,
    formatter,
    fieldDisabled,
    compact,
    expandedRows,
    toggleRowExpansion,
    rowErrors,
    rowIndex,
    storages = [],
    handleStorageChange,
    handlePickedChange,
    handlePriceBlur,
    showWarehousePick = false,
  }) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted

    return (
      <React.Fragment>
        <TableRow
          hover
          key={item.id}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style
          )}
        >
          {props.drag ? (
            <TableCell>
              <DragHandleIcon />
            </TableCell>
          ) : null}

          <TableCell align="left">
            <div
              className={TWclassNames(
                !fieldDisabled
                  ? "cursor-pointer hover:bg-blue-50 p-2 rounded border-2 border-dashed transition-colors"
                  : "cursor-not-allowed p-2 rounded border-2 border-dashed"
              )}
              style={{
                borderColor: rowErrors?.product_id ? '#f87171' : fieldDisabled ? '#e5e7eb' : '#d1d5db',
                backgroundColor: rowErrors?.product_id ? '#fef2f2' : 'inherit',
              }}
              disabled={fieldDisabled}
              onClick={
                fieldDisabled ? () => {} : () => handleRowProductSelect(item)
              }
            >
              {item?.product?.id ? (
                <div>
                  <div
                    className={TWclassNames(
                      fieldDisabled ? "text-gray-400" : "font-bold text-blue-700"
                    )}
                  >
                    {(() => {
                      // Try to construct the best display name from available product data
                      const product = item.product;
                      if (product.displayTitle) {
                        return product.displayTitle;
                      } else if (product.title) {
                        return product.title;
                      } else if (product.item_number) {
                        return `Product ${product.item_number}`;
                      } else if (product.id) {
                        return `Product ID: ${product.id}`;
                      } else {
                        return "Unknown Product";
                      }
                    })()}
                  </div>
                  {item.product?.item_number && (
                    <div className="text-sm text-gray-500 mt-1">
                      Art.nr: {item.product.item_number}
                    </div>
                  )}
                  {item.description && (
                    <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                      {item.description}
                    </div>
                  )}
                </div>
              ) : item?.title ? (
                <div>
                  <div
                    className={TWclassNames(
                      fieldDisabled ? "text-gray-400" : "font-bold text-blue-700"
                    )}
                  >
                    {item.title}
                  </div>
                  {item.description && (
                    <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                      {item.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 italic mt-1">
                    Manuell vare
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 text-center">
                  <div className="text-sm font-medium">
                    + Klikk for å velge produkt
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Velg produkt</div>
                </div>
              )}
            </div>

            <TextField
              disabled={fieldDisabled}
              label="Beskrivelse"
              variant="standard"
              sx={{ 
                mt: "10px",
                "& .MuiInputBase-root": {
                  backgroundColor: rowErrors?.description ? '#fef2f2' : 'inherit',
                  border: rowErrors?.description ? '2px solid #f87171' : 'none',
                }
              }}
              value={item?.description}
              fullWidth
              name="description"
              multiline
              rows={2}
              onChange={(e) => onItemRowChange(e, item)}
            />
          </TableCell>

          {props.quantity && (
            <TableCell
              align="center"
              sx={{ 
                maxWidth: "80px", 
                minWidth: "80px",
              }}
            >
              <div className="flex items-center justify-center space-x-1">
                <TextField
                  disabled={fieldDisabled}
                  variant="standard"
                  value={item?.quantity || ""}
                  name="quantity"
                  onChange={(e) => onItemRowChange(e, item)}
                  onKeyDown={(e) => {
                    // Only allow numbers
                    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    style: { textAlign: "center" },
                    placeholder: "0",
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: rowErrors?.quantity ? '#fef2f2' : 'inherit',
                      border: rowErrors?.quantity ? '2px solid #f87171' : 'none',
                    },
                    width: "40px",
                  }}
                />
                <span className="text-xs text-gray-600 font-medium">
                  {item?.unit?.title || "-"}
                </span>
              </div>
            </TableCell>
          )}

          {props.quantity_picked && (
            <TableCell
              align="center"
              sx={{ maxWidth: "80px", minWidth: "80px" }}
            >
              <div className="flex items-center justify-center space-x-1">
                <TextField
                  disabled={fieldDisabled}
                  variant="standard"
                  value={item?.quantity_picked || ""}
                  name="quantity_picked"
                  onChange={(e) => onItemRowChange(e, item)}
                  onKeyDown={(e) => {
                    // Only allow numbers
                    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    style: { textAlign: "center" },
                    placeholder: "0",
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontSize: "0.875rem",
                    },
                    width: "40px",
                  }}
                />
                <span className="text-xs text-gray-600 font-medium">
                  {item?.unit?.title || "-"}
                </span>
              </div>
            </TableCell>
          )}

          {props.purchase_price ? (
            <TableCell align="left">
              <TextField
                variant="standard"
                value={item?.purchase_price ? formatPriceInput(item.purchase_price) : ""}
                fullWidth
                name="purchase_price"
                onChange={(e) => onItemRowChange(e, item)}
                onBlur={(e) => handlePriceBlur(e, item)}
                onKeyDown={(e) => {
                  // Only allow numbers, comma, and dot (dot will be converted to comma)
                  const allowedKeys = /[0-9,.]/;
                  const isNavigationKey = 
                    e.key === "Backspace" || 
                    e.key === "Delete" || 
                    e.key === "ArrowLeft" || 
                    e.key === "ArrowRight" || 
                    e.key === "Tab" ||
                    e.key === "Enter";
                  
                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                    e.preventDefault();
                  }
                  // Convert dot to comma immediately
                  if (e.key === ".") {
                    e.preventDefault();
                    const syntheticEvent = {
                      target: {
                        name: "purchase_price",
                        value: (e.target.value || "") + ",",
                      },
                    };
                    onItemRowChange(syntheticEvent, item);
                  }
                }}
                disabled={fieldDisabled}
              />
            </TableCell>
          ) : null}

          {props.purchase_price ? (
            <TableCell align="left">
              {formatter.format(calculateRowTotalPurchasePrice(item))}
            </TableCell>
          ) : null}

          {props.sales_price ? (
            <TableCell align="left">
              <TextField
                variant="standard"
                fullWidth
                name="sales_price"
                value={item.sales_price ? formatPriceInput(item.sales_price) : ""}
                onChange={(e) => onItemRowChange(e, item)}
                onBlur={(e) => handlePriceBlur(e, item)}
                onKeyDown={(e) => {
                  // Only allow numbers, comma, and dot (dot will be converted to comma)
                  const allowedKeys = /[0-9,.]/;
                  const isNavigationKey = 
                    e.key === "Backspace" || 
                    e.key === "Delete" || 
                    e.key === "ArrowLeft" || 
                    e.key === "ArrowRight" || 
                    e.key === "Tab" ||
                    e.key === "Enter";
                  
                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                    e.preventDefault();
                  }
                  // Convert dot to comma immediately
                  if (e.key === ".") {
                    e.preventDefault();
                    const syntheticEvent = {
                      target: {
                        name: "sales_price",
                        value: (e.target.value || "") + ",",
                      },
                    };
                    onItemRowChange(syntheticEvent, item);
                  }
                }}
                disabled={fieldDisabled}
              />
            </TableCell>
          ) : null}

          {props.surcharge ? (
            <TableCell align="left">
              <TextField
                variant="standard"
                fullWidth
                name="surcharge"
                value={item.surcharge || ""}
                onChange={(e) => onItemRowChange(e, item)}
                onKeyDown={(e) => {
                  // Only allow numbers
                  if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                    e.preventDefault();
                  }
                }}
                disabled={fieldDisabled}
              />
            </TableCell>
          ) : null}

          {props.discount ? (
            <TableCell align="left">
              <TextField
                variant="standard"
                fullWidth
                name="discount"
                value={item.discount || ""}
                onChange={(e) => onItemRowChange(e, item)}
                onKeyDown={(e) => {
                  // Only allow numbers
                  if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                    e.preventDefault();
                  }
                }}
                disabled={fieldDisabled}
              />
            </TableCell>
          ) : null}

          {props.sales_price ? (
            <TableCell align="left">
              {formatter.format(calculateRowTotalPrice(item))}
            </TableCell>
          ) : null}

          {props.quote_line && !compact && (
            <TableCell align="left">
              <div>
                <PMField
                  type="checkbox"
                  fullWidth
                  name="is_quote_line"
                  checked={item.is_quote_line}
                  onChange={(e) =>
                    onItemRowChange(
                      {
                        target: {
                          name: e.target.name,
                          value: e.target.checked,
                        },
                      },
                      item
                    )
                  }
                />
              </div>
            </TableCell>
          )}

          {showWarehousePick && (
            <TableCell align="left" sx={{ minWidth: "140px", maxWidth: "140px" }}>
              <FormControl
                fullWidth
                variant="standard"
                size="small"
                disabled={fieldDisabled}
                sx={{ 
                  "& .MuiInputBase-root": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  }
                }}
              >
                <InputLabel>Lager</InputLabel>
                <Select
                  value={item?.storage_id || ""}
                  onChange={(e) => handleStorageChange(e, item)}
                  label="Lager"
                >
                  <MenuItem value="">
                    <em>Ingen valgt</em>
                  </MenuItem>
                  {storages.map((storage) => (
                    <MenuItem key={storage.id} value={storage.id}>
                      {storage.title || storage.storage_number || `Lager ${storage.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
          )}

          {showWarehousePick && (
            <TableCell align="left" sx={{ minWidth: "140px", maxWidth: "140px" }}>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center">
                  <PMField
                    type="checkbox"
                    name="picked"
                    checked={item?.picked || false}
                    onChange={(e) =>
                      handlePickedChange(
                        {
                          target: {
                            name: "picked",
                            value: e.target.checked,
                          },
                        },
                        item
                      )
                    }
                    label="Plukket"
                    disabled={fieldDisabled}
                  />
                </div>
                {item?.picked && (
                  <TextField
                    type="date"
                    variant="standard"
                    fullWidth
                    size="small"
                    name="picked_at"
                    value={
                      item?.picked_at
                        ? new Date(item.picked_at).toISOString().split("T")[0]
                        : new Date().toISOString().split("T")[0]
                    }
                    onChange={(e) => onItemRowChange(e, item)}
                    disabled={fieldDisabled}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.7rem",
                      }
                    }}
                    label="Dato"
                  />
                )}
              </div>
            </TableCell>
          )}

          {compact && (
            <TableCell align="center">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(item.draggableId);
                }}
                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
              >
                {expandedRows.has(item.draggableId) ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            </TableCell>
          )}

          <TableCell align="right">
            {item?.id && (
              <TrashIcon
                className="cursor-pointer h-5 h-5 text-red-400"
                onClick={() => deleteRow(item)}
              />
            )}
          </TableCell>
        </TableRow>

        {/* Expanded content for compact mode */}
        {compact && expandedRows.has(item.draggableId) && (
          <TableRow>
            <TableCell colSpan={6} style={{ padding: 0 }}>
              <div className="bg-gray-50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Innkjøpspris
                    </label>
                    <TextField
                      variant="standard"
                      value={item?.purchase_price ? formatPriceInput(item.purchase_price) : ""}
                      fullWidth
                      name="purchase_price"
                      onChange={(e) => onItemRowChange(e, item)}
                      onBlur={(e) => handlePriceBlur(e, item)}
                      onKeyDown={(e) => {
                        // Only allow numbers, comma, and dot (dot will be converted to comma)
                        const allowedKeys = /[0-9,.]/;
                        const isNavigationKey = 
                          e.key === "Backspace" || 
                          e.key === "Delete" || 
                          e.key === "ArrowLeft" || 
                          e.key === "ArrowRight" || 
                          e.key === "Tab" ||
                          e.key === "Enter";
                        
                        if (!allowedKeys.test(e.key) && !isNavigationKey) {
                          e.preventDefault();
                        }
                        // Convert dot to comma immediately
                        if (e.key === ".") {
                          e.preventDefault();
                          const syntheticEvent = {
                            target: {
                              name: "purchase_price",
                              value: (e.target.value || "") + ",",
                            },
                          };
                          onItemRowChange(syntheticEvent, item);
                        }
                      }}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Enhetspris ekskl. mva
                    </label>
                    <TextField
                      variant="standard"
                      fullWidth
                      name="sales_price"
                      value={item.sales_price ? formatPriceInput(item.sales_price) : ""}
                      onChange={(e) => onItemRowChange(e, item)}
                      onBlur={(e) => handlePriceBlur(e, item)}
                      onKeyDown={(e) => {
                        // Only allow numbers, comma, and dot (dot will be converted to comma)
                        const allowedKeys = /[0-9,.]/;
                        const isNavigationKey = 
                          e.key === "Backspace" || 
                          e.key === "Delete" || 
                          e.key === "ArrowLeft" || 
                          e.key === "ArrowRight" || 
                          e.key === "Tab" ||
                          e.key === "Enter";
                        
                        if (!allowedKeys.test(e.key) && !isNavigationKey) {
                          e.preventDefault();
                        }
                        // Convert dot to comma immediately
                        if (e.key === ".") {
                          e.preventDefault();
                          const syntheticEvent = {
                            target: {
                              name: "sales_price",
                              value: (e.target.value || "") + ",",
                            },
                          };
                          onItemRowChange(syntheticEvent, item);
                        }
                      }}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Påslag (%)
                    </label>
                    <TextField
                      variant="standard"
                      fullWidth
                      name="surcharge"
                      value={item.surcharge || ""}
                      onChange={(e) => onItemRowChange(e, item)}
                      onKeyDown={(e) => {
                        // Only allow numbers
                        if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                          e.preventDefault();
                        }
                      }}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Rabatt (%)
                    </label>
                    <TextField
                      variant="standard"
                      fullWidth
                      name="discount"
                      value={item.discount || ""}
                      onChange={(e) => onItemRowChange(e, item)}
                      onKeyDown={(e) => {
                        // Only allow numbers
                        if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                          e.preventDefault();
                        }
                      }}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Tilbudslinje
                    </label>
                    <PMField
                      type="checkbox"
                      name="is_quote_line"
                      checked={item.is_quote_line}
                      onChange={(e) =>
                        onItemRowChange(
                          {
                            target: {
                              name: e.target.name,
                              value: e.target.checked,
                            },
                          },
                          item
                        )
                      }
                    />
                  </div>

                  {showWarehousePick && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                          Lager
                        </label>
                        <FormControl fullWidth variant="standard" size="small">
                          <InputLabel sx={{ fontSize: "0.75rem" }}>Lager</InputLabel>
                          <Select
                            value={item?.storage_id || ""}
                            onChange={(e) => handleStorageChange(e, item)}
                            label="Lager"
                            sx={{
                              "& .MuiSelect-select": {
                                fontSize: "0.875rem",
                                padding: "4px 0 4px 0",
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Ingen valgt</em>
                            </MenuItem>
                            {storages.map((storage) => (
                              <MenuItem key={storage.id} value={storage.id}>
                                {storage.title ||
                                  storage.storage_number ||
                                  `Lager ${storage.id}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                          Plukket
                        </label>
                        <div className="space-y-1.5">
                          <PMField
                            type="checkbox"
                            name="picked"
                            checked={item?.picked || false}
                            onChange={(e) =>
                              handlePickedChange(
                                {
                                  target: {
                                    name: "picked",
                                    value: e.target.checked,
                                  },
                                },
                                item
                              )
                            }
                            label="Plukket"
                          />
                          {item?.picked && (
                            <TextField
                              type="date"
                              variant="standard"
                              fullWidth
                              size="small"
                              name="picked_at"
                              value={
                                item?.picked_at
                                  ? new Date(item.picked_at)
                                      .toISOString()
                                      .split("T")[0]
                                  : new Date().toISOString().split("T")[0]
                              }
                              onChange={(e) => onItemRowChange(e, item)}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontSize: "0.75rem",
                                },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                }
                              }}
                              label="Dato"
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-between text-sm">
                  <span>
                    Beløp innkjøp:{" "}
                    {formatter.format(calculateRowTotalPurchasePrice(item))}
                  </span>
                  <span>
                    Beløp eks. mva:{" "}
                    {formatter.format(calculateRowTotalPrice(item))}
                  </span>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }
);
MemoizedItemRow.displayName = "MemoizedItemRow"; // Add display name

// Helper function to format price input: convert dot to comma and remove non-numeric characters (except comma and dot)
const formatPriceInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  // Remove all characters except numbers, comma, and dot
  const cleaned = stringValue.replace(/[^0-9,.]/g, "");
  // Replace dot with comma for decimal separator
  return cleaned.replace(/\./g, ",");
};

// Helper function to format price with exactly two decimal places
const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  
  // If empty after trimming, return empty string
  if (stringValue === "") return "";
  
  // If value contains a comma, ensure it has exactly two decimal places
  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    const decimalPart = parts[1] || "";
    
    // Pad decimal part to 2 digits or truncate if more than 2
    let formattedDecimal = decimalPart.substring(0, 2);
    if (formattedDecimal.length === 1) {
      // If only one digit, pad with zero: "21,2" -> "21,20"
      formattedDecimal = formattedDecimal + "0";
    } else if (formattedDecimal.length === 0) {
      // If no digits after comma, add "00": "21," -> "21,00"
      formattedDecimal = "00";
    }
    
    return `${integerPart},${formattedDecimal}`;
  }
  
  // If no comma and value is a valid number, add ",00": "100" -> "100,00"
  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }
  
  // Otherwise return as is
  return stringValue;
};

// Helper function to format numeric input: only allow numbers
const formatNumericInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  // Remove all non-numeric characters except for the first minus sign
  const cleaned = stringValue.replace(/[^\d-]/g, "");
  // Ensure minus sign is only at the start
  if (cleaned.startsWith("-")) {
    return cleaned.replace(/-/g, "").replace(/^/, "-");
  }
  return cleaned.replace(/-/g, "");
};

// Helper function to convert comma-formatted string to number
const parseNumericValue = (value) => {
  if (!value || value === "") return 0;
  const stringValue = String(value);
  // Replace comma with dot for parsing
  const normalized = stringValue.replace(/,/g, ".");
  return parseFloat(normalized) || 0;
};

const calculateRowTotalPurchasePrice = (item) => {
  let total = 0.0;
  if (item && item.purchase_price && item.quantity) {
    const purchasePrice = parseNumericValue(item.purchase_price);
    const quantity = parseNumericValue(item.quantity);
    total = purchasePrice * quantity;
  }
  return total;
};

export default function OrderItems(props) {
  const { setFieldValue, values, isLoading, name, drag, disabled, compact, errors } =
    props;
  const dispatch = useDispatch();

  const fieldDisabled = disabled;
  const groupFieldDisabled = disabled;

  const rows = values[name];

  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];

  var formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);

  const orderItems = (rows) => {
    if (!rows) return [];
    return rows.map((row, i) => ({
      ...row,
      row_order: i,
      draggableId: `item-${row.id || i}`, // Use a unique identifier
    }));
  };

  const defaultRows = [
    {
      id: null,
      row_order: null,
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: 0,
      discount: 0,
      surcharge: 0,
      group_row: false,
      unitTitle: "Stk",
    },
  ];

  const mergeItemsFromValues = () => {
    const groupedItems = [];
    const { groups = [], [name]: ungrouped = [] } = values;

    groups.forEach((group) => {
      // Ensure group_row: true on group
      groupedItems.push({
        ...group,
        group_row: true,
      });

      // Ensure group_row: false on child items
      group.items?.forEach((item) => {
        groupedItems.push({
          ...item,
          group_row: false,
          parent_group_id: group.id,
        });
      });
    });

    // Separate ungrouped items and groups without items
    const ungroupedItems = ungrouped.filter((item) => !item.order_group?.id);
    const emptyGroups = groupedItems.filter(
      (group) => group.group_row && !group.items?.length
    );

    // Merge ungrouped items, grouped items, and empty groups
    const merged = [
      ...ungroupedItems, // Ungrouped items always come first
      ...groupedItems.filter((item) => !item.group_row || item.items?.length), // Groups with items
      ...emptyGroups, // Empty groups come last
    ];

    if (merged.length === 0) {
      return defaultRows.map((item, i) => ({
        ...item,
        row_order: i,
        draggableId: `item-default-${i}`,
      }));
    }

    return merged.map((item, i) => ({
      ...item,
      row_order: i, // Reassign row_order to maintain correct order
      draggableId: `item-${item.id || i}`,
    }));
  };

  const [items, setItems] = useState(mergeItemsFromValues());
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  const [itemsVersion, setItemsVersion] = useState(0); // Force re-calculation trigger
  const [showWarehousePickState, setShowWarehousePickState] = useState(false);

  // Check if any product has warehouse enabled or has stock/beholdning
  // Use useEffect to update state when items change
  useEffect(() => {
    // If explicitly set via props, use that
    if (props.showWarehousePick !== undefined) {
      setShowWarehousePickState(props.showWarehousePick);
      return;
    }

    // Check items in the order - use items state first (most up-to-date), then fall back to values
    const allItems = [];
    
    // First check items state (most current)
    if (items && Array.isArray(items) && items.length > 0) {
      items.forEach((item) => {
        if (!item.group_row && item.product) {
          allItems.push(item);
        }
      });
      
      // Also check items in groups from items state
      items.forEach((item) => {
        if (item.group_row && item.items && Array.isArray(item.items)) {
          allItems.push(...item.items);
        }
      });
    }
    
    // Fallback: Add ungrouped items from values if items state is empty
    if (allItems.length === 0 && values[name] && Array.isArray(values[name])) {
      allItems.push(...values[name]);
    }
    
    // Fallback: Add items from groups in values if items state is empty
    if (allItems.length === 0 && values.groups && Array.isArray(values.groups)) {
      values.groups.forEach((group) => {
        if (group.items && Array.isArray(group.items)) {
          allItems.push(...group.items);
        }
      });
    }

    // Check if any item has a product with warehouse=true or has stock
    const hasWarehouseProduct = allItems.some((item) => {
      if (!item.product) return false;
      
      const product = item.product;
      
      // Check if product has warehouse enabled
      // Handle both boolean true and string "true"
      const warehouseValue = product.warehouse === true || product.warehouse === "true" || product.warehouse === 1;
      const warehouseEnabledValue = product.warehouse_enabled === true || product.warehouse_enabled === "true" || product.warehouse_enabled === 1;
      
      if (warehouseValue || warehouseEnabledValue) {
        console.log("✅ Found warehouse product in useEffect:", {
          product_id: product.id,
          warehouse: product.warehouse,
          warehouse_enabled: product.warehouse_enabled,
          warehouseValue,
          warehouseEnabledValue
        });
        return true;
      }
      
      // Check if product has stock/beholdning (if stock field exists)
      if (product.stock !== undefined && product.stock !== null && product.stock > 0) {
        return true;
      }
      
      // Check if product has available_quantity or similar stock-related fields
      if (product.available_quantity !== undefined && product.available_quantity !== null && product.available_quantity > 0) {
        return true;
      }
      
      return false;
    });

    // Debug logging
    console.log("🔍 useEffect checking warehouse:", {
      allItemsCount: allItems.length,
      propsShowWarehousePick: props.showWarehousePick,
      itemsLength: items?.length,
      hasWarehouseProduct,
      itemsVersion
    });
    
    if (allItems.length > 0) {
      const warehouseCheck = allItems.map((item) => ({
        hasProduct: !!item.product,
        product_id: item.product?.id,
        warehouse: item.product?.warehouse,
        warehouse_enabled: item.product?.warehouse_enabled,
        stock: item.product?.stock,
        available_quantity: item.product?.available_quantity,
      }));
      console.log("🔍 useEffect warehouse check details:", {
        allItemsCount: allItems.length,
        hasWarehouseProduct,
        items: warehouseCheck
      });
    }

    console.log("🔍 useEffect setting showWarehousePickState to:", hasWarehouseProduct);
    setShowWarehousePickState(hasWarehouseProduct);
  }, [
    props.showWarehousePick, 
    items, 
    itemsVersion,
    // Use JSON.stringify to ensure deep comparison triggers re-calculation
    JSON.stringify(items?.map(item => ({
      id: item.id,
      product_id: item.product_id,
      product_warehouse: item.product?.warehouse,
      product_warehouse_enabled: item.product?.warehouse_enabled
    }))),
    values[name], 
    values.groups, 
    name
  ]);

  const showWarehousePick = props.showWarehousePick !== undefined ? props.showWarehousePick : showWarehousePickState;
  
  console.log("🔍 Final showWarehousePick value:", {
    propsShowWarehousePick: props.showWarehousePick,
    showWarehousePickState,
    finalShowWarehousePick: showWarehousePick,
    itemsVersion,
    itemsLength: items?.length
  });

  // Debug logging (can be removed later)
  useEffect(() => {
    const allItems = [];
    if (items && Array.isArray(items) && items.length > 0) {
      items.forEach((item) => {
        if (!item.group_row && item.product) {
          allItems.push(item);
        }
      });
      items.forEach((item) => {
        if (item.group_row && item.items && Array.isArray(item.items)) {
          allItems.push(...item.items);
        }
      });
    }
    
    const warehouseItems = allItems.filter((item) => {
      if (!item.product) return false;
      const product = item.product;
      return product.warehouse === true || product.warehouse_enabled === true ||
             (product.stock !== undefined && product.stock !== null && product.stock > 0) ||
             (product.available_quantity !== undefined && product.available_quantity !== null && product.available_quantity > 0);
    });

    console.log("🔍 Warehouse pick check:", {
      showWarehousePick,
      itemsCount: items?.length || 0,
      allItemsCount: allItems.length,
      itemsWithProducts: allItems.filter(item => item.product).length,
      warehouseProducts: warehouseItems.length,
      warehouseItems: warehouseItems.map(item => ({
        id: item.id,
        product_id: item.product_id,
        product_warehouse: item.product?.warehouse,
        product_warehouse_enabled: item.product?.warehouse_enabled,
        product_stock: item.product?.stock,
        product_available_quantity: item.product?.available_quantity,
        product: item.product
      }))
    });
  }, [showWarehousePick, items]);

  useEffect(() => {
    if (showWarehousePick && storages.length === 0) {
      dispatch(getStorages());
    }
  }, [showWarehousePick, dispatch, storages.length]);

  // Define columns after showWarehousePick is calculated
  const columns = React.useMemo(() => [
    { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
    { id: "product", label: "Produkt", minWidth: 200, active: true },
    {
      id: "quantity",
      label: "Antall/Enhet",
      width: "80px !important",
      minWidth: "80px",
      maxWidth: 80,
      active: props.quantity ? true : false,
    },
    {
      id: "quantity_picked",
      label: "Plukket/Enhet",
      width: "80px !important",
      minWidth: "80px",
      maxWidth: 80,
      active: props.quantity_picked ? true : false,
    },
    {
      id: "purchase_price",
      label: "Innkjøpspris",
      minWidth: 50,
      align: "left",
      active: props.purchase_price && !compact ? true : false,
    },
    {
      id: "purchase_price_total",
      label: "Beløp innkjøp",
      minWidth: 50,
      align: "left",
      active: props.purchase_price && !compact ? true : false,
    },
    {
      id: "sales_price_ex_vat",
      label: "Enhetspris ekskl. mva",
      minWidth: 50,
      align: "left",
      active: props.sales_price && !compact ? true : false,
    },
    {
      id: "surcharge",
      label: "Påslag (%)",
      minWidth: 50,
      align: "left",
      active: props.surcharge && !compact ? true : false,
    },
    {
      id: "discount",
      label: "Rabatt (%)",
      minWidth: 50,
      align: "left",
      active: props.discount && !compact ? true : false,
    },
    {
      id: "total_price",
      label: "Beløp eks. mva",
      minWidth: 50,
      align: "left",
      active: props.sales_price && !compact ? true : false,
    },
    {
      id: "is_quote_line",
      label: "Tilbudslinje",
      minWidth: 50,
      align: "left",
      active: props.quote_line && !compact ? true : false,
    },
    {
      id: "warehouse",
      label: "Lager",
      minWidth: 150,
      align: "left",
      active: showWarehousePick ? true : false,
    },
    {
      id: "picked",
      label: "Plukket",
      minWidth: 120,
      align: "left",
      active: showWarehousePick ? true : false,
    },
    {
      id: "expand",
      label: "",
      minWidth: 30,
      maxWidth: 30,
      active: compact,
    },
  ], [drag, props.quantity, props.quantity_picked, props.purchase_price, props.sales_price, props.surcharge, props.discount, props.quote_line, compact, showWarehousePick]);

  const activeColumns = columns.filter((column) => column.active == true);

  useEffect(() => {
    // Don't run this effect when we're updating product data
    if (isUpdatingProduct) return;

    const groups = [];
    const ungrouped = [];
    let currentGroup = null;

    items.forEach((item) => {
      if (item.group_row) {
        currentGroup = { ...item, items: [] };
        groups.push(currentGroup);
      } else if (currentGroup && item.parent_temp_id === currentGroup.temp_id) {
        currentGroup.items.push(item);
      } else {
        ungrouped.push(item);
      }
    });

    const filteredGroups = groups.map((g) => ({
      ...g,
      items: g.items,
    }));

    setFieldValue("groups", filteredGroups);
    setFieldValue(name, ungrouped);
  }, [items, isUpdatingProduct]);

  const addItemRow = (type) => {
    const tempId = uuidv4();
    const lastItem = items[items.length - 1];
    const isLastItemGroup = lastItem?.group_row === true;

    const newRow = {
      id: null, // Backend skal sette dette senere
      temp_id: tempId, // Brukes internt
      title: "",
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: 0,
      discount: 0,
      surcharge: 0,
      group_row: type === "group",
      draggableId: `item-${tempId}`,
      row_order: items.length,
      parent_temp_id:
        type === "group" ? null : isLastItemGroup ? lastItem.temp_id : null,
    };

    setItems((prev) => [...prev, newRow]);
  };

  const handleStorageChange = (e, item) => {
    const newValue = e.target.value;
    onItemRowChange(
      {
        target: {
          name: "storage_id",
          value: newValue === "" ? null : parseInt(newValue),
        },
      },
      item
    );
  };

  const handlePickedChange = (e, item) => {
    const isPicked = e.target.value === true || e.target.value === "true" || e.target.checked === true;
    const updates = {
      picked: isPicked,
    };
    // If picking, set picked_at to today if not already set
    if (isPicked && !item.picked_at) {
      updates.picked_at = new Date().toISOString();
    }
    // If unpicking, clear picked_at
    if (!isPicked) {
      updates.picked_at = null;
    }

    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      ...updates,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
    // Force re-calculation of showWarehousePick
    setItemsVersion(prev => prev + 1);
    
    // Also update form values
    const currentGroups = values.groups || [];
    const currentUngrouped = values[name] || [];
    
    // Find if this item belongs to a group
    let itemInGroup = false;
    let updatedGroups = currentGroups.map((group) => {
      if (group.items) {
        const updatedItems = group.items.map((groupItem) => {
          if (
            groupItem.id === updatedItem.id ||
            groupItem.temp_id === updatedItem.temp_id
          ) {
            itemInGroup = true;
            return updatedItem;
          }
          return groupItem;
        });
        return { ...group, items: updatedItems };
      }
      return group;
    });
    
    // If item is not in a group, update the ungrouped items
    let updatedUngrouped = currentUngrouped;
    if (!itemInGroup) {
      updatedUngrouped = currentUngrouped.map((unGroupItem) => {
        if (
          unGroupItem.id === updatedItem.id ||
          unGroupItem.temp_id === updatedItem.temp_id
        ) {
          return updatedItem;
        }
        return unGroupItem;
      });
    }
    
    // Update form values
    setFieldValue("groups", updatedGroups);
    setFieldValue(name, updatedUngrouped);
  };

  const handlePriceBlur = (e, data) => {
    const fieldName = e.target.name;
    let inputValue = e.target.value;
    
    // Format price with two decimals when field loses focus
    if (fieldName === "purchase_price" || fieldName === "sales_price") {
      if (inputValue && inputValue.trim() !== "") {
        // Format all price values to have two decimals
        inputValue = formatPriceWithTwoDecimals(inputValue);
        
        // Update the field value
        const syntheticEvent = {
          target: {
            name: fieldName,
            value: inputValue,
          },
        };
        onItemRowChange(syntheticEvent, data);
      }
    }
  };

  const onItemRowChange = (e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    let inputValue = e.target.value;
    const fieldName = e.target.name;

    // Format input based on field type
    if (fieldName === "purchase_price" || fieldName === "sales_price") {
      // Price fields: convert dot to comma
      inputValue = formatPriceInput(inputValue);
    } else if (
      fieldName === "quantity" ||
      fieldName === "quantity_picked" ||
      fieldName === "surcharge" ||
      fieldName === "discount"
    ) {
      // Numeric fields: only allow numbers
      inputValue = formatNumericInput(inputValue);
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data.row_order],
      [fieldName]: inputValue,
    };
    prevItems[data.row_order] = item;
    setItems(prevItems);

    // Clear validation errors for this field when it becomes valid
    if (props.setFieldError && props.errors?.[name]?.[data.row_order]) {
      const rowIndex = data.row_order;
      const currentErrors = props.errors[name] || [];
      
      if (currentErrors[rowIndex]) {
        // Check if either product_id or description is now valid
        const hasValidProduct = item.product_id && item.product_id.trim() !== '';
        const hasValidDescription = item.description && item.description.trim() !== '';
        const hasValidQuantity = item.quantity && item.quantity > 0;
        
        // Clear product_id and description errors if either one is valid
        if (hasValidProduct || hasValidDescription) {
          if (currentErrors[rowIndex].product_id) {
            props.setFieldError(`${name}.${rowIndex}.product_id`, undefined);
          }
          if (currentErrors[rowIndex].description) {
            props.setFieldError(`${name}.${rowIndex}.description`, undefined);
          }
        }
        
        // Clear quantity error if it's valid
        if (hasValidQuantity && currentErrors[rowIndex].quantity) {
          props.setFieldError(`${name}.${rowIndex}.quantity`, undefined);
        }
      }
    }
  };

  const calculateRowTotalPrice = (item) => {
    const salesPrice = parseNumericValue(item.sales_price);
    const salesPriceDivided = salesPrice / 100;
    const surcharge = item.surcharge ? salesPriceDivided * parseNumericValue(item.surcharge) : 0;
    const discount = item.discount ? salesPriceDivided * parseNumericValue(item.discount) : 0;
    var total = 0;
    if (item && salesPrice && item.quantity) {
      const quantity = parseNumericValue(item.quantity);
      total =
        (salesPrice +
          surcharge -
          discount) *
        quantity;
    }

    return total;
  };

  const calculateTotalPrice = (items) => {
    var total = 0;
    if (items.length > 0) {
      items?.forEach((item) => {
        if (!item.delete && item.group_row == false) {
          total += calculateRowTotalPrice(item);
        }
      });
    }
    return total;
  };

  const calculateRowTotalPurchasePrice = (item) => {
    var total = 0.0;
    if (item && item.purchase_price && item.quantity) {
      const purchasePrice = parseNumericValue(item.purchase_price);
      const quantity = parseNumericValue(item.quantity);
      total = purchasePrice * quantity;
    }

    return total;
  };

  const calculateTotalPurchasePrice = (items) => {
    var total = 0;
    if (items?.length > 0) {
      items?.forEach((item) => {
        if (!item.delete && item.group_row == false) {
          total += calculateRowTotalPurchasePrice(item);
        }
      });
    }

    return total;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    let currentGroup = null;

    const updatedItems = reorderedItems.map((item, index) => {
      if (item.group_row) {
        currentGroup = item;
        return {
          ...item,
          row_order: index,
          parent_group_id: null,
        };
      } else {
        return {
          ...item,
          row_order: index,
          parent_group_id: currentGroup?.id ?? null,
        };
      }
    });

    setItems(updatedItems);
  };

  const mapSelectedProductsFromItems = () => {
    // Only return the product for the currently selected row, not all products
    if (selectedItemRow && items && items[selectedItemRow.row_order]) {
      const currentItem = items[selectedItemRow.row_order];
      if (currentItem.product) {
        return [currentItem.product];
      }
    }

    return [];
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const handleRowProductSelect = (row) => {
    setSelectedItemRow(row);
    setProductSelectorOpen(true);
  };

  const handleProductSelect = async (data) => {
    setProductSelectorOpen(false);

    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error("No product data received:", data);
      return;
    }

    if (selectedItemRow) {

      // Set flag to prevent useEffect from running
      setIsUpdatingProduct(true);

      let prevItems = [...items];
      const selectedProduct = data[0];

      if (!selectedProduct || !selectedProduct.id) {
        console.error("Invalid product data:", selectedProduct);
        setIsUpdatingProduct(false);
        return;
      }

      // Fetch full product details with all necessary fields
      let fullProductData = selectedProduct;
      try {
        const fields = "id,title,displayTitle,item_number,purchase_price,sales_price,unit_id,unit(*),warehouse,warehouse_enabled,stock,available_quantity,short_description,brand,status,product_type";
        const productResponse = await fetchProduct(selectedProduct.id, { fields });
        if (productResponse && productResponse.id) {
          fullProductData = productResponse;
          console.log("✅ Fetched full product data:", {
            id: fullProductData.id,
            warehouse: fullProductData.warehouse,
            warehouse_enabled: fullProductData.warehouse_enabled,
            stock: fullProductData.stock,
            available_quantity: fullProductData.available_quantity,
            fullProduct: fullProductData
          });
        } else {
          console.log("⚠️ Product response missing id:", productResponse);
        }
      } catch (error) {
        console.error("Error fetching full product details:", error);
        // Continue with basic product data if fetch fails
      }

      let item = {
        ...prevItems[selectedItemRow.row_order],
        product: fullProductData,
        product_id: fullProductData.id,
        unit_id: fullProductData.unit?.id || fullProductData.unit_id,
        unit: fullProductData.unit,
        sales_price: fullProductData.sales_price,
        purchase_price: fullProductData.purchase_price,
        // Only set quantity to 1 if it's not already set or is 0
        quantity:
          prevItems[selectedItemRow.row_order].quantity > 0
            ? prevItems[selectedItemRow.row_order].quantity
            : 1,
      };
      
      console.log("🔍 Setting item with product:", {
        item_id: item.id,
        product_id: item.product_id,
        product_warehouse: item.product?.warehouse,
        product_warehouse_enabled: item.product?.warehouse_enabled,
        fullProduct: item.product
      });
      
      prevItems[selectedItemRow.row_order] = item;
      setItems(prevItems);
      // Force re-calculation of showWarehousePick
      setItemsVersion(prev => prev + 1);
      
      console.log("🔍 Items after update:", prevItems.map(i => ({
        id: i.id,
        product_id: i.product_id,
        product_warehouse: i.product?.warehouse,
        product_warehouse_enabled: i.product?.warehouse_enabled
      })));

      // Also update the form values directly to ensure they persist
      const currentGroups = values.groups || [];
      const currentUngrouped = values[name] || [];

      // Find if this item belongs to a group
      let itemInGroup = false;
      let updatedGroups = currentGroups.map((group) => {
        if (group.items) {
          const updatedItems = group.items.map((groupItem) => {
            if (
              groupItem.id === item.id ||
              groupItem.temp_id === item.temp_id
            ) {
              itemInGroup = true;
              return item;
            }
            return groupItem;
          });
          return { ...group, items: updatedItems };
        }
        return group;
      });

      // If item is not in a group, update the ungrouped items
      let updatedUngrouped = currentUngrouped;
      if (!itemInGroup) {
        updatedUngrouped = currentUngrouped.map((unGroupItem) => {
          if (
            unGroupItem.id === item.id ||
            unGroupItem.temp_id === item.temp_id
          ) {
            return item;
          }
          return unGroupItem;
        });
      }

      // Update form values
      setFieldValue("groups", updatedGroups);
      setFieldValue(name, updatedUngrouped);

             // Reset selectedItemRow after successful product selection
       setSelectedItemRow(null);

       // Clear validation errors for product_id and description when product is selected
       if (props.setFieldError && props.errors?.[name]) {
         const currentErrors = props.errors[name] || [];
         if (currentErrors[selectedItemRow.row_order]) {
           const rowErrors = currentErrors[selectedItemRow.row_order];
           
           // Clear product_id error since it's now valid
           if (rowErrors.product_id) {
             props.setFieldError(`${name}.${selectedItemRow.row_order}.product_id`, undefined);
           }
           
           // Clear description error if product_id is now valid
           if (rowErrors.description && item.product_id) {
             props.setFieldError(`${name}.${selectedItemRow.row_order}.description`, undefined);
           }
         }
       }

       // Reset flag after a short delay to allow state updates to complete
       setTimeout(() => {
         setIsUpdatingProduct(false);
       }, 100);
    } else {
      console.error("🔍 OrderItems - No selectedItemRow found");
    }
  };

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    console.log("selectedItemRow", selectedItemRow);

    if (selectedItemRow) {
      const newArray = items.map((item) => {
        // If the selected item is a group, mark the group and its children as deleted
        if (
          item.id === selectedItemRow.id ||
          item.draggableId === selectedItemRow.draggableId
        ) {
          if (item.group_row) {
            // Mark the group and its children as deleted
            return {
              ...item,
              delete: true,
              items: item.items?.map((child) => ({ ...child, delete: true })),
            };
          }
          // Mark a standalone item as deleted
          return { ...item, delete: true };
        }

        // If the item is a child of the selected group, mark it as deleted
        if (item.parent_group_id === selectedItemRow.id) {
          return { ...item, delete: true };
        }

        return item; // Leave other items unchanged
      });

      setItems(orderItems(newArray)); // Update the state with the modified array
    }
  };

  const deleteRow = (item) => {
    setSelectedItemRow(item);
    setDeleteConfirmationOpen(true);
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      <Table sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) =>
              column.active ? (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    top: 57,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ) : null
            )}
          </TableRow>
        </TableHead>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {[...items]
                  .sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0))
                  .map((item, index) => (
                    <Draggable
                      key={item.draggableId} // Ensure key is consistent
                      draggableId={item.draggableId} // Ensure draggableId is unique
                      index={index}
                    >
                      {(provided, snapshot) =>
                        item.group_row == true ? (
                          props.group ? (
                            <MemoizedGroupRow
                              item={item}
                              provided={provided}
                              snapshot={snapshot}
                              deleteRow={deleteRow}
                              onItemRowChange={onItemRowChange}
                              groupFieldDisabled={groupFieldDisabled}
                            />
                          ) : null
                        ) : (
                          <MemoizedItemRow
                            item={item}
                            provided={provided}
                            snapshot={snapshot}
                            props={props}
                            deleteRow={deleteRow}
                            onItemRowChange={onItemRowChange}
                            handleRowProductSelect={handleRowProductSelect}
                            calculateRowTotalPrice={calculateRowTotalPrice}
                            formatter={formatter}
                            fieldDisabled={fieldDisabled}
                            compact={compact}
                            expandedRows={expandedRows}
                            toggleRowExpansion={toggleRowExpansion}
                            rowErrors={errors?.[name]?.[index]}
                            rowIndex={index}
                            storages={storages}
                            handleStorageChange={handleStorageChange}
                            handlePickedChange={handlePickedChange}
                            handlePriceBlur={handlePriceBlur}
                            showWarehousePick={showWarehousePick}
                          />
                        )
                      }
                    </Draggable>
                  ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        {/* Display row-specific errors */}
        {errors?.[name] && errors[name].some((itemErrors, index) => itemErrors && Object.keys(itemErrors).length > 0) && (
          <TableRow>
            <TableCell colSpan={activeColumns.length} sx={{ padding: 0 }}>
              <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Radfeil
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {errors[name].map((itemErrors, index) => {
                        if (!itemErrors || Object.keys(itemErrors).length === 0) return null;
                        return Object.keys(itemErrors).map((fieldName) => {
                          const fieldError = itemErrors[fieldName];
                          if (fieldError) {
                            return (
                              <p key={`row-${index}-${fieldName}`} className="mb-1">
                                • Rad {index + 1}: {fieldError}
                              </p>
                            );
                          }
                          return null;
                        });
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}

        <TableFooter>
          <TableRow>
            <TableCell align="left" colSpan={compact ? 4 : 6}>
              <Button onClick={() => addItemRow()}>Ny rad</Button>
            </TableCell>
            {props.group ? (
              <TableCell align="left" colSpan={compact ? 3 : 5}>
                <Button onClick={() => addItemRow("group")}>
                  Ny gruppering
                </Button>
              </TableCell>
            ) : null}
          </TableRow>

          {/* Compact View Summary Row */}
          <TableRow>
            <TableCell align="left" colSpan={compact ? 1 : 2}>
              <span className="text-sm font-medium text-gray-700">
                Total enhetspris ekskl. mva:
              </span>
            </TableCell>
            <TableCell align="left" colSpan={1}></TableCell>

            <TableCell align="left" colSpan={1}></TableCell>
            <TableCell align="left" colSpan={3}>
              <span className="text-sm font-medium text-gray-700">
                {formatter.format(calculateTotalPrice(items))}
              </span>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      <ItemSelectorV2
        {...props}
        multiple={false}
        open={productSelectorOpen}
        onChange={(data) => {
          handleProductSelect(data);
        }}
        module={"product"}
        selectedItems={mapSelectedProductsFromItems()}
        autoClose={true}
        compact={true}
        onClose={() => {
          setProductSelectorOpen(false);
          // Reset selectedItemRow when dialog is closed without selection
          setSelectedItemRow(null);
        }}
      />

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        yesCallback={handleDeleteRow}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

OrderItems.defaultProps = {
  group: false,
  purchase_price: false,
  sales_price: false,
  name: "items",
  drag: true,
  quantity_picked: false,
  quantity: true,
  showWarehousePick: false,
};
